<template>
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    :options-data="cardOptionsWithCSVExportOption"
    :has-not-classified="hasNotClassified"
    @call-option-menu="callOptionMenu"
    @reload="$emit('reload', $event)"
    @on-show-not-classified="onShowNotClassified"
  >
    <v-row
      no-gutters
      class="pie-chart-container"
    >
      <div class="pie-chart">
        <div class="pie-chart__chart-area">
          <HighCharts
            v-if="series.length"
            :ref="chartRef"
            :key="chartRef"
            :options="chartOptions"
            :callback="highchartsCallBack"
            class="pie-chart__highchart"
          />
        </div>
        <div class="pie-chart__aggregation">
          <h3 class="pie-chart__title">
            {{ sumSeries.toLocaleString('pt-BR') }}
          </h3>
          <p class="pie-chart__subtitle">
            {{ aggregationText }}
          </p>
        </div>
      </div>
      <div class="pie-chart-legend">
        <v-simple-table class="pie-chart-legend__table">
          <tbody>
            <tr
              v-for="(legend, index) in legendData"
              :key="index"
              :class="`pie-chart-legend__row${isNotVisible(legend)}`"
              @click="toggleLegend(legend)"
              @mouseover="mouseOverLegend(legend)"
              @mouseleave="mouseLeaveLegend(legend)"
            >
              <td class="pie-chart-legend__cell-icon">
                <v-icon
                  :color="`${legend.visible? legend.color: disableColor}`"
                >
                  mdi-circle-medium
                </v-icon>
              </td>
              <td class="pie-chart-legend__cell-name">
                {{ legend.name }}
              </td>
              <td class="pie-chart-legend__cell-percent">
                {{ legend.percentage ? cellPercentage(legend.percentage) : 0 }}%
              </td>
              <td class="pie-chart-legend__cell-value">
                {{ cellValue(legend.y) }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </v-row>

    <template #top-right-slot>
      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import { Chart } from 'highcharts-vue';
import highChartsBasics from '@/mixins/highcharts-basics';
import cardsModel from '@/mixins/cards-model';
import exportModel from '@/mixins/export-model';
import merge from 'lodash/merge';
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import { formatNumber, formatSocialNumberWithDecimal } from '@/utils/formatter';
import Badge from '@/components/Badge';

const AGGREGATION_DATA_TYPE = {
  listening: 'publicações',
  sac: 'interações',
};

const POLARITY_ORDER = {
  'Positivo': 1,
  'Neutro': 2,
  'Negativo': 3,
  'Não Classificados': 4,
  'Não classificado': 5,
};

export default {
  name: 'PieChart',
  components: {
    HighCharts: Chart,
    CardWithTitle,
    Badge,
  },
  mixins: [
    highChartsBasics,
    cardsModel,
    exportModel,
  ],
  props: {
    requestStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    isPolarity: {
      type: Boolean,
      default: false,
    },
    series: {
      type: [Array, Object],
      default: () => ([]),
    },
    cardOptions: {
      type: [Array, Object],
      default: () => ([]),
    },
    fieldLabel: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      disableColor: 'var(--ds-color-gray-1)',
      hideNotClassified: true,
      notClassifiedLabels: ['Não Classificados', 'Não classificado'],
      notShowClassifiedLabel: 'Mostrar não classificados',
      showClassifiedLabel: 'Esconder não classificados',
      legendData: null,
    };
  },
  computed: {
    seriesData() {
      if (!this.isPolarity) {
        return this.getFormattedSeries().sort((actualValue, prevValue) => {
          if (actualValue.y === prevValue.y) return 0;
          return actualValue.y > prevValue.y ? -1 : 1;
        });
      }
      return this.getFormattedSeries().sort((actualValue, prevValue) => this.sortSeries(actualValue, prevValue));
    },
    chartOptions() {
      const customOptions = {
        chart: {
          type: 'pie',
          plotBackgroundColor: null,
          plotBorderWidth: 0,
          plotShadow: false,
          spacing: [0, 0, 0, 0],
          margin: [0, 0, 0, 0],
        },
        plotOptions: {
          pie: {
            borderColor: 'black',
            borderWidth: 0.5,
            showInLegend: false,
          },
        },
        tooltip: {
          enabled: false,
        },
        series: [{
          data: this.seriesData,
          innerSize: '65%',
          dataLabels: {
            enabled: false,
          },
        }],
        legend: {},
      };
      return merge({}, this.highchartsBasics, this.highchartTheme, customOptions, this.cardOptions);
    },
    hasNotClassified() {
      return Boolean(this.series.find(series => this.notClassifiedLabels.includes(series.name)));
    },
    sumSeries() {
      return this.series.
        filter(serie => this.hideNotClassified ? !this.notClassifiedLabels.includes(serie.name) : true).
        reduce((result, { y }) => result + y, 0);
    },
    aggregationText() {
      const fieldName = this.fieldLabel || AGGREGATION_DATA_TYPE[this.card.dataType];
      return `${this.$t('components.models.PieChart.label.aggregationText')} ${fieldName}`;
    },
  },
  watch: {
    chartOptions() {
      this.$nextTick(() => {
        this.highchartsCallBack(this.$refs[this.chartRef].chart);
      });
    },
  },
  methods: {
    cellValue(value) {
      return formatSocialNumberWithDecimal(value);
    },
    cellPercentage(value) {
      return formatNumber(value, { minimumFractionDigits: 0,
        maximumFractionDigits: 2 });
    },
    getFormattedSeries() {
      if (this.hasNotClassified && this.hideNotClassified) {
        return this.series.filter(series => !this.notClassifiedLabels.includes(series.name));
      }
      return this.series;
    },
    onShowNotClassified() {
      this.hideNotClassified = !this.hideNotClassified;
    },
    sortSeries(actualValue, prevValue) {
      if (POLARITY_ORDER[actualValue.name] === POLARITY_ORDER[prevValue.name]) return 0;
      return POLARITY_ORDER[actualValue.name] < POLARITY_ORDER[prevValue.name] ? -1 : 1;
    },
    highchartsCallBack(chart) {
      this.legendData = chart?.series[0]?.data.filter(serie => Boolean(serie.y));
    },
    toggleLegend(legend) {
      legend.setVisible(!legend.visible);
    },
    isNotVisible(legend) {
      return !legend.visible ? '-disable' : '';
    },
    mouseOverLegend(legend) {
      legend.setState('hover');
    },
    mouseLeaveLegend(legend) {
      legend.setState();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~vuetify/src/styles/main.sass';
  $legend-height: 72px;

  .pie-chart-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: hidden;
    justify-content: flex-start;
  }

  .pie-chart {
    display: flex;
    width: 100%;
    height: calc(100% - #{$legend-height});
    font-weight: var(--ds-font-weight-regular);

    &__chart-area {
      min-width: 149px;
      min-height: 133px;
      width: 40%;
    }

    &__highchart {
      height: 100%;
    }

    &__aggregation {
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-left: var(--ds-spacing-stack-xs);
    }

    &__title {
      color: var(--ds-color-text-1);
      font-size: var(--ds-font-size-md);
      font-weight: var(--ds-font-weight-semibold);
    }

    &__subtitle {
      color: var(--ds-color-text-2);
      font-size: var(--ds-font-size-xs);
      line-height: var(--ds-font-line-height-slim);
    }

    &--with-not-classified {
      height: calc(100% - 36px) !important;
    }

    &__data-label {
      font-size: 12px;
    }
  }

  .pie-chart-legend {
    width: auto;
    height: $legend-height;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      border: none;
      background-color: var(--ds-color-gray-1);
    }

    &__table {
      font-size: var(--ds-font-size-xs);
      font-weight: var(--ds-font-weight-semibold);
      color: var(--ds-color-text-2) !important;
    }

    &__row {
      border-bottom: none !important;

      &:hover {
        cursor: pointer;
        background-color: transparent !important;
        color: var(--ds-color-text-1) !important;
      }
    }

    &__row-disable {
      color: var(--ds-color-gray-3);

      &:hover {
        cursor: pointer;
        background-color: transparent !important;
        color: var(--ds-color-text-1) !important;
      }
    }

    &__cell-icon {
      height: auto !important;
      border-bottom: none !important;
      padding: 0 var(--ds-spacing-stack-quark) 0 0 !important;
      width: 24px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: var(--ds-font-size-xs) !important;
      line-height: var(--ds-font-line-height-slim);
    }

    &__cell-name {
      height: auto !important;
      border-bottom: none !important;
      padding: 0 var(--ds-spacing-stack-nano) 0 0 !important;
      max-width: 120px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: var(--ds-font-size-xs) !important;
      line-height: var(--ds-font-line-height-slim);
    }

    &__cell-percent {
      height: auto !important;
      border-bottom: none !important;
      padding: 0 var(--ds-spacing-stack-nano) 0 0 !important;
      text-overflow: ellipsis;
      text-align: right;
      font-size: var(--ds-font-size-xs) !important;
      line-height: var(--ds-font-line-height-slim);
    }

    &__cell-value {
      height: auto !important;
      border-bottom: none !important;
      padding: 0 var(--ds-spacing-stack-sm) 0 0 !important;
      text-align: right;
      font-size: var(--ds-font-size-xs) !important;
      line-height: var(--ds-font-line-height-slim);
    }
  }

  ::v-deep {
    .highcharts-container,
    svg {
      overflow: visible !important;
    }

    .v-data-table {
      width: auto !important;
    }

    .v-data-table > .v-data-table__wrapper {
      width: auto !important;
    }

    .v-data-table > .v-data-table__wrapper > table {
      width: auto;
    }

    .theme--dark.v-data-table {
      background-color: transparent !important;
    }

    .theme--light.v-data-table {
      background-color: transparent !important;
    }
  }
</style>
